import React, { useState } from 'react';
import { Form, Button, Row, Col, Table, InputGroup } from 'react-bootstrap';
import { AppointmentType } from '../..//types/AppointmentType'; // Adjust the path if needed
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import DurationSelect from '../duration-select/DurationSelect';

type Props = {
  clinicId: string;
  initialTypes?: AppointmentType[];
  onChange?: (updatedTypes: AppointmentType[]) => void;
};

const AppointmentTypesManager: React.FC<Props> = ({ clinicId, initialTypes = [], onChange }) => {
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>(initialTypes);
  const [newType, setNewType] = useState<Omit<AppointmentType, '_id'>>({
    name: '',
    durationMinutes: null,
  });

  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'he';

  const handleAddType = () => {
    if (!newType.name || !newType.durationMinutes) return;
    const updated = [...appointmentTypes, { ...newType }];
    setAppointmentTypes(updated);
    setNewType({ name: '', durationMinutes: null });
    onChange?.(updated);
  };

  const handleDelete = (index: number) => {
    const updated = appointmentTypes.filter((_, i) => i !== index);
    setAppointmentTypes(updated);
    onChange?.(updated);
  };

  const handleInputChange = (field: keyof AppointmentType, value: string | number | null) => {
    setNewType({ ...newType, [field]: value });
  };

return (
  <div>
    <h5 className="mb-3">{t('appointment-types')}</h5>
    <Row className="align-items-end">
      {/* Treatment Name */}
      <Col xs={12} md={4} className="mb-3 mb-md-0">
        <InputGroup>
          <Form.Control
            type="text"
            className="input-bg-dark text-white border-0 shadow-sm"
            placeholder={t('enter-appointment-type-name')}
            value={newType.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />
        </InputGroup>
      </Col>

      {/* Duration */}
      <Col md={2}>
        <Form.Group controlId="appointmentTypeDuration">
          <InputGroup>
            <DurationSelect
              value={newType.durationMinutes ?? ''}
              onChange={(e) => handleInputChange('durationMinutes', Number(e.target.value))}
              isRtl={isRtl}
              t={t}
            />
          </InputGroup>
        </Form.Group>
      </Col>

      {/* Add Button */}
      <Col md={6} className="mt-2 mt-md-0">
        <Button variant="primary" onClick={handleAddType}>
          {t('add-type')}
        </Button>
      </Col>
    </Row>

    {/* List of existing types as rows */}
    {appointmentTypes.length > 0 && (
      <div className="mt-4">
        {appointmentTypes.map((type, index) => (
          <Row key={index} className="align-items-center py-2 border-bottom">
            <Col xs={12} md={4} className="fw-bold">
              {type.name}
            </Col>
            <Col xs={6} md={2}>
              {type.durationMinutes} {t('minutes')}
            </Col>
            <Col xs={6} md={6} className="mt-2 mt-md-0">
              <Button variant="danger" size="sm" onClick={() => handleDelete(index)}>
                <i className="mdi mdi-delete-variant btn-icon-prepend"></i> {t('delete')}
              </Button>
            </Col>
          </Row>
        ))}
      </div>
    )}
  </div>
);

};

export default AppointmentTypesManager;
